import { Box, IconButton, type Theme, Tooltip } from '@mui/material';
import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { Fullscreen } from 'lucide-react';
import { TbRulerMeasure } from 'react-icons/tb';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
    histogramHintEnabledState,
    scaleDisplayEnabledState,
    zoomToCursorState,
} from '~/state/atoms/toolsAtoms';

const toggleButtonSx = (active: boolean) => (theme: Theme) => ({
    width: 28,
    height: 28,
    borderRadius: 1,
    color: active ? theme.palette.primary.main : theme.palette.text.primary,
    backgroundColor: active ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
    '& svg': {
        width: '100%',
        height: '100%',
        fontSize: 'inherit',
    },
});

export default function PanToolOptions() {
    const [zoomToCursor, setZoomToCursor] = useRecoilState(zoomToCursorState);
    const [scaleDisplayEnabled, setScaleDisplayEnabled] = useRecoilState(
        scaleDisplayEnabledState
    );
    const setHistogramEnabled = useSetRecoilState(histogramHintEnabledState);
    const intl = useIntl();

    // Track the live value in a ref so rapid consecutive clicks compute
    // `next` from the actual current state, not a stale render-time value —
    // otherwise a quick double-click can apply the same toggle twice and fail
    // to flip. Mirrors the histogram tool's toggle.
    const scaleDisplayEnabledRef = useRef(scaleDisplayEnabled);
    scaleDisplayEnabledRef.current = scaleDisplayEnabled;

    // Ruler and histogram share the bottom band; enabling the ruler turns
    // the histogram off so toggling either always has a visible effect.
    const toggleScaleDisplay = () => {
        const next = !scaleDisplayEnabledRef.current;
        scaleDisplayEnabledRef.current = next;
        setScaleDisplayEnabled(next);
        if (next) {
            setHistogramEnabled(false);
        }
    };

    const zoomToCursorLabel = intl.formatMessage({
        id: 'toolbar.navigation.zoomToCursor.tooltip',
    });
    const scaleOverlayLabel = intl.formatMessage({
        id: 'toolbar.navigation.scaleOverlay.tooltip',
    });

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
            }}
        >
            <Tooltip title={zoomToCursorLabel} placement="bottom">
                <IconButton
                    size="small"
                    aria-label={zoomToCursorLabel}
                    aria-pressed={zoomToCursor}
                    onClick={() => setZoomToCursor(!zoomToCursor)}
                    sx={toggleButtonSx(zoomToCursor)}
                >
                    <Fullscreen />
                </IconButton>
            </Tooltip>
            <Tooltip title={scaleOverlayLabel} placement="bottom">
                <IconButton
                    size="small"
                    aria-label={scaleOverlayLabel}
                    aria-pressed={scaleDisplayEnabled}
                    onClick={toggleScaleDisplay}
                    sx={toggleButtonSx(scaleDisplayEnabled)}
                >
                    <TbRulerMeasure />
                </IconButton>
            </Tooltip>
        </Box>
    );
}
